/**
 * Grade Colour Palette section — CR-7079.
 *
 * The one place a customer sets grade colours. Before this, the same colour had
 * to be set separately under OTIF Settings, PPM Settings and Grade Thresholds,
 * and the Supplier Performance table ignored all three. Saving a colour here
 * recolours that grade on every scorecard that shows it.
 */
import { useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Loader2, RotateCcw, Check, Palette } from 'lucide-react';

import { SupplierPerformanceSettings } from '../../../lib/api';
import { useGradeColourPalette } from './useGradeColourPalette';

interface GradeColourPaletteSectionProps {
  settings: SupplierPerformanceSettings | null;
  /** Reload settings so every band picks up its new `effective_color`. */
  onSaved: () => void;
  /** Non-admins see the palette but cannot change it. */
  readOnly?: boolean;
}

export function GradeColourPaletteSection({
  settings,
  onSaved,
  readOnly = false,
}: GradeColourPaletteSectionProps) {
  /** The customer's own name for a grade, so the palette reads as they do. */
  const labelFor = useCallback(
    (grade: string): string => {
      const band = settings?.rating_thresholds?.find((t) => t.grade === grade);
      return band?.display_label || band?.label || grade;
    },
    [settings],
  );

  const { palette, drafts, loading, saving, resetting, setDraft, save, resetDefaults, isDirty } =
    useGradeColourPalette(onSaved, labelFor);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Grade Colours
          </CardTitle>
          <CardDescription>
            Set each grade&apos;s colour once. It is applied everywhere that grade is
            shown — Supplier Performance, the dashboard delivery and quality tiles,
            OTIF and PPM.
          </CardDescription>
        </div>
        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void resetDefaults()}
            disabled={resetting || loading}
          >
            {resetting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            <span className="ml-2">Reset to Defaults</span>
          </Button>
        )}
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading grade colours…
          </div>
        ) : palette.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">
            No grade colours configured yet. Open Grade Thresholds to create the
            grades, then set their colours here.
          </p>
        ) : (
          <div className="space-y-3">
            {palette.map((entry) => {
              const value = drafts[entry.grade] ?? entry.color;
              const dirty = isDirty(entry);
              return (
                <div key={entry.id} className="flex items-center gap-3">
                  <div
                    className="h-9 w-9 shrink-0 rounded border"
                    style={{ backgroundColor: value }}
                    aria-hidden="true"
                  />
                  <div className="w-40 shrink-0">
                    <span className="text-sm font-medium">{labelFor(entry.grade)}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      Grade {entry.grade}
                    </span>
                  </div>

                  <Label htmlFor={`grade-colour-${entry.id}`} className="sr-only">
                    {labelFor(entry.grade)} colour
                  </Label>
                  <Input
                    id={`grade-colour-${entry.id}`}
                    type="color"
                    value={value}
                    disabled={readOnly}
                    onChange={(e) => setDraft(entry.grade, e.target.value.toUpperCase())}
                    className="h-9 w-14 p-1"
                  />
                  <Input
                    aria-label={`${labelFor(entry.grade)} hex colour`}
                    value={value}
                    disabled={readOnly}
                    onChange={(e) => setDraft(entry.grade, e.target.value.toUpperCase())}
                    placeholder="#D4AF37"
                    className="w-32 font-mono"
                  />

                  {!readOnly && (
                    <Button
                      size="sm"
                      onClick={() => void save(entry)}
                      disabled={!dirty || saving}
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      <span className="ml-2">Save</span>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
